import CalendarBlue from "calendar blue.svg";
import CalendarGreen from "calendar green.svg";
import profileActions from "actions/profile";
import cn from "classnames";
import TariffSelectionFinancialForm from "components/modal/tariffSelectionFinancialForm";
import Loader from "components/shared/loader";
import { dateToView } from "helpers/date";
import { documentStatusesEnum, organizationDemoPeriodStatusesEnum, tariffTypesEnum } from "helpers/enums";
import { ending } from "helpers/i18n";
import { paths } from "helpers/paths";
import { connect } from "helpers/redux";
import LineUp from "lineup.svg";
import Tooltip from "rc-tooltip";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getDocumentPdf, getSubscriptions } from "sources/billing";
import { getServices, subscribe } from "sources/crStart";
import { deferTariffSelection } from "sources/profile/organization";

const FEATURES = [
    { text: "14 дней бесплатно", icon: "icon-uEA54-checkmark" },
    { icon: "icon-uEA54-checkmark", dynamic: true },
    { text: "Полный доступ к сервису", icon: "icon-uEA54-checkmark" },
    { text: "Количество пользователей не ограничено", icon: "icon-uEA54-checkmark" },
    { text: "Автопродление", icon: "icon-uEA54-checkmark" },
];

const DEFAULT_VEHICLE_LIMIT = 10;
const CR_START_5_VEHICLE_LIMIT = 5;
const FINANCIAL_REQUIRED_FIELDS = ["bankId", "bank", "ks", "rs"];

const invoiceStepModalClasses = {
    invoiceCreated: "modal-window--tariff-invoice-created",
    invoiceLocationHint: "modal-window--tariff-invoice-location",
};

const getLatestUnpaidDocument = (documents = []) =>
    [...documents]
        .filter((document) => document.status === documentStatusesEnum.Unpaid)
        .sort((a, b) => {
            const dateDiff = new Date(b.createDate || 0) - new Date(a.createDate || 0);

            return dateDiff || (b.id || 0) - (a.id || 0);
        })[0] || null;

const openPdfBlobInNewTab = (blob) => {
    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

const hasUnfilledFinancialFields = (organization) =>
    FINANCIAL_REQUIRED_FIELDS.some((field) => !String(organization?.[field] || "").trim());

const hasSubscribeData = (period) =>
    period?.tariffServiceId !== undefined &&
    period?.tariffServiceId !== null &&
    period?.periodId !== undefined &&
    period?.periodId !== null;

const getPeriodLengthInMonths = (period) =>
    period.periodType === "Year" ? period.periodLength * 12 : period.periodLength;

const getVehicleLimit = (period) =>
    period?.tariffType === tariffTypesEnum.CrStart5 ? CR_START_5_VEHICLE_LIMIT : DEFAULT_VEHICLE_LIMIT;

const getFeatureText = (feature, periodInfo) =>
    feature.dynamic
        ? `${periodInfo.totalMonths * 30} дней с момента оплаты до ${periodInfo.vehicleLimit} ТС`
        : feature.text;

const sortTariffServices = (services) => {
    const sortedServices = [...(services || [])].sort(
        (a, b) => getPeriodLengthInMonths(a) - getPeriodLengthInMonths(b),
    );
    const crStart5Services = sortedServices.filter((service) => service.tariffType === tariffTypesEnum.CrStart5);

    if (!crStart5Services.length) {
        return sortedServices;
    }

    const otherServices = sortedServices.filter((service) => service.tariffType !== tariffTypesEnum.CrStart5);
    const middleIndex = Math.ceil(otherServices.length / 2);

    return [...otherServices.slice(0, middleIndex), ...crStart5Services, ...otherServices.slice(middleIndex)];
};

function TariffSelectionForm({
    daysRemaining,
    status,
    hasDocument,
    onSkip,
    onSubscribeSuccess,
    onOpenBillingDocuments,
    getOrg,
    back,
    organization,
}) {
    const flowRootRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [tariffServices, setTariffServices] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [currentStep, setCurrentStep] = useState("selection");
    const [invoiceDocument, setInvoiceDocument] = useState(null);
    const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
    const [invoicePdfBlob, setInvoicePdfBlob] = useState(null);
    const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
    const [invoicePreviewError, setInvoicePreviewError] = useState(false);

    useEffect(() => {
        getServices()
            .then((res) => {
                setTariffServices(res || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                setLoadError(true);
            });
    }, []);

    useEffect(() => {
        if (currentStep !== "invoiceCreated") {
            return undefined;
        }

        let cancelled = false;

        const loadInvoicePreview = async () => {
            setInvoicePreviewLoading(true);
            setInvoicePreviewError(false);
            setInvoiceDocument(null);
            setInvoicePdfUrl(null);
            setInvoicePdfBlob(null);

            try {
                const subscriptionsInfo = await getSubscriptions();
                const unpaidDocument = getLatestUnpaidDocument(subscriptionsInfo?.documents);

                if (cancelled) {
                    return;
                }

                setInvoiceDocument(unpaidDocument);

                if (!unpaidDocument?.id) {
                    return;
                }

                const pdf = await getDocumentPdf(unpaidDocument.id);

                if (cancelled) {
                    window.URL.revokeObjectURL(pdf.url);
                    return;
                }

                setInvoicePdfUrl(pdf.url);
                setInvoicePdfBlob(pdf.blob);
            } catch (_err) {
                if (!cancelled) {
                    setInvoicePreviewError(true);
                }
            } finally {
                if (!cancelled) {
                    setInvoicePreviewLoading(false);
                }
            }
        };

        loadInvoicePreview();

        return () => {
            cancelled = true;
        };
    }, [currentStep]);

    useEffect(() => {
        const modalClass = invoiceStepModalClasses[currentStep];

        if (!modalClass) {
            return undefined;
        }

        const modalWindow = flowRootRef.current?.closest(".modal-window");
        modalWindow?.classList.add(modalClass);

        return () => {
            modalWindow?.classList.remove(modalClass);
        };
    }, [currentStep]);

    useEffect(() => {
        return () => {
            if (invoicePdfUrl) {
                window.URL.revokeObjectURL(invoicePdfUrl);
            }
        };
    }, [invoicePdfUrl]);

    const handleSkip = () => {
        back();
        deferTariffSelection();
        if (onSkip) {
            onSkip();
        }
    };

    const handleOpenBillingDocuments = () => {
        if (onOpenBillingDocuments) {
            onOpenBillingDocuments();
        }
        back();
    };

    const handleOpenInvoicePdf = () => {
        if (invoicePdfBlob) {
            openPdfBlobInNewTab(invoicePdfBlob);
            return;
        }

        if (!invoiceDocument?.id) {
            return;
        }

        setInvoicePreviewLoading(true);
        setInvoicePreviewError(false);
        getDocumentPdf(invoiceDocument.id)
            .then((pdf) => {
                setInvoicePdfUrl(pdf.url);
                setInvoicePdfBlob(pdf.blob);
                openPdfBlobInNewTab(pdf.blob);
            })
            .catch(() => setInvoicePreviewError(true))
            .finally(() => setInvoicePreviewLoading(false));
    };

    const handleShowInvoiceLocationHint = () => {
        setInvoicePdfUrl(null);
        setInvoicePdfBlob(null);
        setCurrentStep("invoiceLocationHint");
    };

    const subscribeToPeriod = (period) => {
        if (!hasSubscribeData(period)) {
            return Promise.resolve();
        }
        setLoading(true);
        return subscribe({ tariffServiceId: period.tariffServiceId, periodId: period.periodId })
            .then(() => {
                getOrg();
                setLoading(false);
                setCurrentStep("invoiceCreated");
                if (onSubscribeSuccess) {
                    onSubscribeSuccess();
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const handleSubscribe = (period) => {
        if (!hasSubscribeData(period)) {
            return;
        }

        setSelectedPeriod(period);

        if (hasUnfilledFinancialFields(organization)) {
            setCurrentStep("financial");
            return;
        }

        subscribeToPeriod(period);
    };

    const getPeriodInfo = (period, basePrice, hasCrStart5Tariff) => {
        const { periodLength, periodType } = period;
        const isCrStart5 = period.tariffType === tariffTypesEnum.CrStart5;
        const vehicleLimit = getVehicleLimit(period);
        const isYear = periodType === "Year" || (periodType === "Month" && periodLength === 12);
        const isThreeMonths = periodType === "Month" && periodLength === 3;
        const isMonth = periodType === "Month" && periodLength === 1;

        let title = "";
        if (isYear) {
            title = "1 год";
        } else if (periodType === "Month") {
            title = `${periodLength} ${ending(periodLength, "месяц", "месяца", "месяцев")}`;
        } else {
            title = `${periodLength} ${periodType === "Month" ? "мес." : "дн."}`;
        }

        const currentMonths = periodType === "Year" ? periodLength * 12 : periodLength;
        const pricePerMonth = period.price / currentMonths;

        let savings = null;
        if (!hasCrStart5Tariff && !isCrStart5 && basePrice && pricePerMonth < basePrice) {
            savings = (basePrice - pricePerMonth) * 12;
        }

        const cardTitle = isCrStart5
            ? "Для компаний до 5 машин"
            : hasCrStart5Tariff
              ? `Тариф до ${vehicleLimit} машин`
              : title;

        if (isMonth) {
            return {
                title,
                cardTitle,
                description: "Для знакомства с сервисом",
                icon: <CalendarGreen className="tariff-icon" />,
                isPopular: false,
                savings: null,
                pricePerMonth,
                totalMonths: currentMonths,
                vehicleLimit,
            };
        }

        if (isThreeMonths) {
            return {
                title,
                cardTitle,
                description: isCrStart5
                    ? "Оптимальный старт без переплаты"
                    : "Оптимальный выбор для постоянных пользователей",
                icon: <LineUp className="tariff-icon" />,
                isPopular: true,
                popularBadge: isCrStart5 ? "Оптимальный тариф" : "Популярный тариф",
                buttonText: savings ? "Выбрать и сэкономить" : "Подключить",
                savings,
                pricePerMonth,
                totalMonths: currentMonths,
                vehicleLimit,
            };
        }

        if (isYear) {
            return {
                title,
                cardTitle,
                description: "Для постоянных пользователей",
                icon: <CalendarBlue className="tariff-icon" />,
                isPopular: false,
                savings,
                pricePerMonth,
                totalMonths: currentMonths,
                vehicleLimit,
            };
        }

        return {
            title,
            cardTitle,
            description: "",
            icon: "icon-uEA60-calendar",
            iconClass: "",
            isPopular: false,
            savings,
            pricePerMonth,
            totalMonths: currentMonths,
            vehicleLimit,
        };
    };

    const isExpired = status === organizationDemoPeriodStatusesEnum.Expired;

    if (loading && !tariffServices) {
        return <Loader />;
    }

    if (loadError && !tariffServices) {
        return (
            <div className="tariff-selection-modal position-relative">
                <div className="modal-header-text">
                    <h2>Не удалось загрузить тарифы</h2>
                    <p>Пожалуйста, попробуйте позже</p>
                </div>
            </div>
        );
    }

    if (isExpired && hasDocument) {
        return (
            <div className="tariff-selection-modal position-relative">
                <div className="modal-header-text p-5">
                    <h2>Бесплатный период истек</h2>
                    <p>
                        Для продолжения работы необходимо оплатить выставленный счет.&nbsp;
                        <Link onClick={handleOpenBillingDocuments} to={paths.profileBillingDocuments}>
                            Перейти к счету
                        </Link>
                    </p>
                </div>
            </div>
        );
    }

    if (!isExpired && hasDocument) {
        return (
            <div className="tariff-selection-modal position-relative">
                <div className="modal-header-text p-5">
                    <h2>Счет выставлен</h2>
                    <p>
                        Счет на оплату уже выставлен. Пожалуйста, оплатите его для активации тарифа.{" "}
                        <Link onClick={handleOpenBillingDocuments} to={paths.profileBillingDocuments}>
                            Перейти к счету
                        </Link>
                    </p>
                </div>
            </div>
        );
    }

    const services = sortTariffServices(tariffServices);

    const basePrice = services.find((p) => p.periodType === "Month" && p.periodLength === 1)?.price;
    const hasCrStart5Tariff = services.some((service) => service.tariffType === tariffTypesEnum.CrStart5);

    const renderFeaturesTooltip = (periodInfo) => (
        <div className="tariff-summary-card__features-tooltip">
            {FEATURES.map((feature, idx) => (
                <div key={idx} className="tariff-summary-card__feature">
                    <i className={feature.icon} />
                    <span>{getFeatureText(feature, periodInfo)}</span>
                </div>
            ))}
        </div>
    );

    const renderTariffSummary = (period) => {
        const info = getPeriodInfo(period, basePrice, hasCrStart5Tariff);

        return (
            <div className="tariff-summary-card">
                <div className="tariff-summary-card__title">
                    <i className="icon-uEA60-calendar" />
                    Тариф {info.title}
                </div>
                <div className="tariff-summary-card__subtitle">Ежемесячная подписка</div>
                <div className="tariff-summary-card__price">{info.pricePerMonth.toLocaleString()} ₽/мес.</div>
                <div className="tariff-summary-card__total">
                    {period.price.toLocaleString()} ₽ за {info.title}
                </div>
                <div className="tariff-summary-card__limit">до {info.vehicleLimit} ТС</div>
                <Tooltip overlay={renderFeaturesTooltip(info)} placement="top">
                    <button className="tariff-summary-card__link" type="button">
                        <i className="icon-uEA88-info-circle" />
                        Что входит в тариф
                    </button>
                </Tooltip>
            </div>
        );
    };

    const renderInvoicePreviewMessage = () => {
        if (invoicePreviewLoading) {
            return "Загружаем предпросмотр счета";
        }

        if (invoicePreviewError) {
            return "Предпросмотр временно недоступен";
        }

        if (!invoiceDocument) {
            return "Счет уже выставлен и появится в разделе оплаты";
        }

        return "PDF счета можно открыть в новой вкладке";
    };

    const renderInvoiceDocumentInfo = () => {
        if (!invoiceDocument) {
            return "Доступ откроется сразу после оплаты";
        }

        const number = invoiceDocument.number ? `№ ${invoiceDocument.number}` : "";
        const date = invoiceDocument.createDate ? `от ${dateToView(invoiceDocument.createDate)}` : "";
        const documentInfo = [number, date].filter(Boolean).join(" ");

        return documentInfo ? `Счет ${documentInfo}` : "Счет на оплату";
    };

    if (currentStep === "financial" && selectedPeriod) {
        return (
            <TariffSelectionFinancialForm
                loading={loading}
                organization={organization}
                period={selectedPeriod}
                periodInfo={getPeriodInfo(selectedPeriod, basePrice, hasCrStart5Tariff)}
                features={FEATURES}
                onContinue={() => subscribeToPeriod(selectedPeriod)}
                onDefer={() => subscribeToPeriod(selectedPeriod)}
            />
        );
    }

    if (currentStep === "invoiceCreated" && selectedPeriod) {
        return (
            <div
                ref={flowRootRef}
                className={cn("tariff-flow-modal tariff-invoice-created", { "loading loading--small": loading })}
            >
                <div className="tariff-flow-modal__header">
                    <h2>Счет выставлен</h2>
                    <p>Счет доступен в личном кабинете в разделе оплаты</p>
                </div>

                <div className="tariff-flow-modal__body">
                    {renderTariffSummary(selectedPeriod)}
                    <div
                        className={cn("tariff-invoice-created__panel", {
                            "loading loading--small": invoicePreviewLoading,
                        })}
                    >
                        {invoicePdfUrl ? (
                            <iframe
                                className="tariff-invoice-created__pdf"
                                src={invoicePdfUrl}
                                title="Предпросмотр счета на оплату"
                                sandbox="allow-downloads allow-same-origin"
                            />
                        ) : (
                            <div className="tariff-invoice-created__preview tariff-invoice-created__preview--placeholder">
                                <div className="tariff-invoice-created__preview-title">Счет на оплату</div>
                                <div className="tariff-invoice-created__preview-text">
                                    {renderInvoicePreviewMessage()}
                                </div>
                                <div
                                    className={cn(
                                        "tariff-invoice-created__preview-line",
                                        "tariff-invoice-created__preview-line--lg",
                                    )}
                                />
                                <div className="tariff-invoice-created__preview-line" />
                                <div className="tariff-invoice-created__preview-line" />
                            </div>
                        )}
                        <div className="tariff-invoice-created__note">
                            <div>{renderInvoiceDocumentInfo()}</div>
                            <div>Доступ откроется сразу после оплаты</div>
                            <div className="tariff-invoice-created__actions">
                                <button
                                    className="tariff-invoice-created__action"
                                    disabled={invoicePreviewLoading || (!invoiceDocument?.id && !invoicePdfBlob)}
                                    type="button"
                                    onClick={handleOpenInvoicePdf}
                                >
                                    Открыть PDF
                                </button>
                                <Link onClick={handleOpenBillingDocuments} to={paths.profileBillingDocuments}>
                                    Перейти к счету
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <button className="tariff-financial-form__defer" type="button" onClick={handleShowInvoiceLocationHint}>
                    Позже <span>(Счет сохранится)</span>
                </button>
            </div>
        );
    }

    if (currentStep === "invoiceLocationHint") {
        return (
            <div ref={flowRootRef} className="tariff-invoice-location">
                <h2>Счет выставлен</h2>
                <p>Можно перейти в личный кабинет, в подраздел “Оплата”</p>
                <img
                    className="tariff-invoice-location__image"
                    src="/images/tariff-selection/billing-header-menu.png"
                    alt="Где найти счет в разделе оплаты"
                />
                <button className="tariff-invoice-location__button" type="button" onClick={back}>
                    Понятно
                </button>
            </div>
        );
    }

    return (
        <div className={cn("tariff-selection-modal position-relative", { loading })}>
            <div className="modal-header-text">
                <h2>
                    {isExpired
                        ? "Бесплатный период истек"
                        : daysRemaining !== undefined
                          ? `Через ${daysRemaining} ${ending(
                                daysRemaining,
                                "день",
                                "дня",
                                "дней",
                            )}, истекает бесплатный период`
                          : "Выберите тарифный план"}
                </h2>
                <p>Выберите тот тариф, который подходит именно вам</p>
            </div>

            <div className="tariffs-container">
                {services.map((period) => {
                    const info = getPeriodInfo(period, basePrice, hasCrStart5Tariff);

                    return (
                        <div
                            key={`${period.tariffServiceId}-${period.periodId}`}
                            className={`tariff-card ${info.isPopular ? "tariff-card--popular" : ""}`}
                        >
                            {info.isPopular && <div className="popular-badge">{info.popularBadge}</div>}
                            {info.icon}
                            <div className="tariff-title">{info.cardTitle}</div>
                            <div className="tariff-price">{info.pricePerMonth.toLocaleString()} Р/мес.</div>
                            <div className="tariff-subprice">
                                ({period.price.toLocaleString()} Р за {info.title})
                            </div>
                            <div className="tariff-duration">{info.totalMonths * 30} дней</div>

                            <div className="tariff-description">{info.description}</div>

                            {info.savings && (
                                <div className="savings-banner">
                                    Вы экономите {info.savings.toLocaleString()} Р в год! Отличный выбор для постоянных
                                    пользователей
                                </div>
                            )}

                            <div className="features-list">
                                {FEATURES.map((feature, idx) => (
                                    <div key={idx} className="feature-item">
                                        <i className="icon-uEA54-checkmark" />
                                        <span>
                                            {getFeatureText(feature, info)}
                                        </span>
                                    </div>
                                ))}
                                {info.savings && (
                                    <div className="feature-item feature-item--highlight">
                                        <i className="icon-uEA54-checkmark" />
                                        <span>Экономия {info.savings.toLocaleString()} Р в год!</span>
                                    </div>
                                )}
                            </div>

                            <button
                                className={`btn-connect ${info.isPopular ? "btn-connect--popular" : ""}`}
                                onClick={() => handleSubscribe(period)}
                            >
                                {info.buttonText || "Подключить"}
                            </button>

                            {info.isPopular && (
                                <div className="popular-hint">8 из 10 пользователей выбирают этот тариф</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {onSkip && !isExpired && (
                <button className="later-btn" onClick={handleSkip}>
                    Позже
                </button>
            )}
        </div>
    );
}

export default connect(
    (state) => ({
        organization: state.profile.organization,
    }),
    (dispatch) => ({ getOrg: () => dispatch(profileActions.getOrg()) }),
)(TariffSelectionForm);
